import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { trackAddToCart } from '@/lib/analytics';

const KEY = 'rp_cart_v1';
const CartContext = createContext(null);

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

// Each cart line: { key, product_id, variant_vid, name, variant_name, price, image, qty }
export function CartProvider({ children }) {
  const [items, setItems] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage full/blocked */ }
  }, [items]);

  const add = (product, qty = 1, variant = null) => {
    const key = `${product.id}::${variant?.vid || ''}`;
    setItems(prev => {
      const found = prev.find(i => i.key === key);
      if (found) {
        return prev.map(i => i.key === key ? { ...i, qty: Math.min(20, i.qty + qty) } : i);
      }
      return [...prev, {
        key,
        product_id: product.id,
        sku: product.sku || null, // Meta catalog retailer id
        variant_vid: variant?.vid || null,
        name: product.name,
        variant_name: variant?.name || null,
        price: Number(variant?.price ?? product.price ?? 0),
        image: variant?.image || product.image || null,
        qty: Math.min(20, qty),
      }];
    });
    toast.success(`Added to cart — ${product.name}`, {
      description: variant?.name || undefined,
      action: { label: 'View cart', onClick: () => { window.location.href = '/cart'; } },
    });
    trackAddToCart(product, qty, variant?.price ?? product.price);
  };

  const setQty = (key, qty) => {
    setItems(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(1, Math.min(20, qty)) } : i));
  };

  const remove = (key) => setItems(prev => prev.filter(i => i.key !== key));
  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((a, i) => a + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((a, i) => a + i.price * i.qty, 0), [items]);

  const value = useMemo(() => ({ items, add, setQty, remove, clear, count, subtotal }), [items, count, subtotal]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}

// Used by the payment-success page (outside hook contexts is fine too)
export function clearStoredCart() {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}
